import { useState } from 'react';

export default function LifeStageSelector() {
  const [selectedStage, setSelectedStage] = useState(0);

  const stages = [
    {
      id: 0,
      title: 'Just Started Earning',
      icon: '🎓',
      description: 'Beginning your financial journey',
      needs: [
        'Term life insurance for future family protection',
        'Health insurance to cover medical emergencies',
        'Start SIP with small amounts (₹1000-5000/month)',
        'Emergency fund building',
      ],
      priority: 'Build a strong foundation for future wealth',
    },
    {
      id: 1,
      title: 'Family Protection',
      icon: '👨‍👩‍👧',
      description: 'Securing your loved ones',
      needs: [
        'High coverage term insurance (10-15x annual income)',
        'Comprehensive health insurance for family',
        'Child education planning through mutual funds',
        'Increase SIP contributions',
      ],
      priority: 'Ensure family is financially protected in your absence',
    },
    {
      id: 2,
      title: 'Wealth Building',
      icon: '📈',
      description: 'Growing your assets',
      needs: [
        'Diversified mutual fund portfolio',
        'Systematic SIP investments',
        'Tax-saving instruments (ELSS)',
        'Review and increase insurance coverage',
      ],
      priority: 'Create wealth through disciplined investing',
    },
    {
      id: 3,
      title: 'Child Future Planning',
      icon: '🎯',
      description: 'Securing your children\'s dreams',
      needs: [
        'Education fund planning',
        'Child-specific insurance plans',
        'Long-term equity investments',
        'Dedicated SIPs for education goals',
      ],
      priority: 'Ensure children\'s education and marriage needs are met',
    },
    {
      id: 4,
      title: 'Retirement Planning',
      icon: '🏖️',
      description: 'Financial freedom in golden years',
      needs: [
        'Retirement-focused mutual funds',
        'Senior citizen health insurance',
        'Debt-free planning',
        'Pension and annuity plans',
      ],
      priority: 'Build a corpus for comfortable retirement',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Find the Right Plan for Your{' '}
            <span className="text-blue-600">Life Stage</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Every stage of life has unique financial needs. Select your stage to see
            personalized recommendations.
          </p>
        </div>

        {/* Stage Selector - Horizontal Scroll */}
        <div className="mb-12 relative">
          {/* Scroll hint indicator for mobile */}
          <div className="md:hidden absolute right-0 top-0 bottom-4 w-16 bg-gradient-to-l from-blue-50 to-transparent pointer-events-none z-10"></div>
          
          <div className="overflow-x-auto overflow-y-hidden pb-4 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="flex space-x-4 min-w-max md:justify-center px-4 md:px-0">
              {stages.map((stage) => (
                <button
                  key={stage.id}
                  onClick={() => setSelectedStage(stage.id)}
                  className={`flex-shrink-0 px-6 py-4 rounded-2xl transition-all duration-300 ${
                    selectedStage === stage.id
                      ? 'bg-blue-600 text-white shadow-lg scale-105'
                      : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md'
                  }`}
                >
                  <div className="text-3xl mb-2">{stage.icon}</div>
                  <div className="font-semibold text-sm whitespace-nowrap">
                    {stage.title}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content Display */}
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-gray-100">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Left Column */}
            <div>
              <div className="flex items-center space-x-4 mb-6">
                <div className="text-6xl">{stages[selectedStage].icon}</div>
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                    {stages[selectedStage].title}
                  </h3>
                  <p className="text-gray-600">{stages[selectedStage].description}</p>
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded-r-lg mb-6">
                <div className="font-semibold text-blue-900 mb-1">Priority Focus:</div>
                <p className="text-blue-800">{stages[selectedStage].priority}</p>
              </div>
            </div>

            {/* Right Column */}
            <div>
              <h4 className="font-semibold text-xl text-gray-900 mb-4">
                Recommended Financial Solutions:
              </h4>
              <ul className="space-y-3">
                {stages[selectedStage].needs.map((need, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <svg
                      className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-gray-700">{need}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                <a
                  href="#contact"
                  className="inline-block bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 transition-all shadow-md hover:shadow-lg font-semibold"
                >
                  Get Personalized Plan
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
